import { z } from "zod";
import { callSequenceViewSchema } from "./call-sequence.js";
import { leadCallingSchema, NO_CALLING } from "./callability.js";
import { filterDefinitionSchema, filterFieldRefSchema, filterJsonSchema } from "./filters.js";
import { actorViewSchema, personSchema } from "./people.js";
import { taskViewSchema } from "./task-view.js";
import { ID_PATTERN, idSchema, multiLineTextSchema, singleLineTextSchema } from "./text.js";
import { leadWhatsAppSchema, NO_LEAD_WHATSAPP } from "./whatsapp.js";
// Lettura dei Lead (piano M2, Task 6): elenco con ricerca, «Di chi», ordinamento e pagine; scheda del Lead con il
// modello pubblicato, le statistiche delle chiamate e i permessi di chi guarda; cataloghi e comuni. Il perimetro
// di chi guarda lo applica il server dentro la query, prima di ricerca, ordinamento, conteggi e pagine.
//
// Testi e identificativi seguono le regole di `text.ts` (decisione del 1° ottobre 2026, «i caratteri dei testi scritti a
// mano»), che fermano un NUL prima della query (un 500 altrimenti): i nomi (del titolare, del secondo intestatario, del
// salone) e il sito, che si mostra come un collegamento (questione aperta n. 92), sono testi su una riga, dove un a capo
// o un tab diventa uno spazio e i caratteri invisibili o che girano il verso si rifiutano; gli altri testi (indirizzo,
// CAP, valori dei campi dell'azienda, note, motivo, ricerche) tengono tab e a capo e rifiutano gli altri caratteri di
// controllo, e nei testi su una riga il servizio unisce poi le righe in uno spazio come prima; gli identificativi hanno
// la forma di `ID_PATTERN`. Un campo che si svuota ammette il testo vuoto (`allowEmpty`), come prima.
/** Un nome del Lead, che si può svuotare (`""` o `null`). */
const leadNameSchema = (max) => singleLineTextSchema(max, { allowEmpty: true });
/**
 * Un altro testo, che può essere vuoto: un campo del Lead che si svuota (tab e a capo li unisce in uno spazio il
 * servizio) o una ricerca (le parole le separa la normalizzazione della ricerca, tab e a capo compresi), anche quella
 * della Timeline.
 */
export const freeTextSchema = (max) => multiLineTextSchema(max, { allowEmpty: true });
/**
 * Le righe che la pagina di un elenco può saltare al massimo: oltre è `422`. Nessuno sfoglia un milione di righe, e una
 * query non deve scorrerne di più per arrivare a una pagina vuota.
 */
export const LIST_OFFSET_MAX = 1_000_000;
/** `offset` di un elenco a pagine: da 0 (di partenza) a `LIST_OFFSET_MAX`. */
export const listOffsetSchema = z.coerce.number().int().min(0).max(LIST_OFFSET_MAX).default(0);
/**
 * Le colonne per cui l'elenco si ordina: i valori vuoti vanno in fondo in entrambe le direzioni. `lastName` e `firstName`
 * sono Cognome e Nome (decisione del 6 ottobre 2026); `lead`, nome e cognome insieme (o il salone), era la colonna «Lead»
 * e resta per le Smart List salvate e per gli indirizzi di prima, anche se nessuna colonna lo offre più.
 */
export const LEAD_SORT_FIELDS = [
    "lead",
    "lastName",
    "firstName",
    "city",
    "status",
    "latestCallAt",
    "outgoingCallCount",
    "outgoingCallCountLast12Months",
    "outgoingCallCountLast4Months",
    "createdAt",
    "owner",
];
/** Quanti campi dell'azienda `GET /api/leads?fields=` restituisce al massimo: le colonne mostrate. */
export const LEAD_LIST_FIELDS_MAX = 20;
/**
 * Le chiavi del parametro `fields`, nell'ordine scritto e senza doppioni; il testo vuoto non ne ha nessuna. Il contratto
 * ha già controllato che ognuna abbia la forma di un identificativo (`ID_PATTERN`) e che non siano più di
 * `LEAD_LIST_FIELDS_MAX`, doppioni compresi.
 */
export function leadListFieldKeys(fields) {
    return fields === "" ? [] : [...new Set(fields.split(","))];
}
/**
 * Le colonne di sistema che costano una lettura in più (LEAD-13): «In Smart List» (`smartLists`) e «Provenienza» (`source`).
 * Arrivano con le righe soltanto se chieste con `include=`, come i campi dell'azienda con `fields=`: l'interfaccia le chiede
 * quando la colonna si vede.
 */
export const LEAD_LIST_INCLUDES = ["smartLists", "source"];
const isLeadListInclude = (value) => LEAD_LIST_INCLUDES.includes(value);
/** Le colonne del parametro `include`, senza doppioni; il testo vuoto non ne ha nessuna. Il contratto le ha già controllate. */
export function leadListIncludes(include) {
    return include === "" ? [] : [...new Set(include.split(",").filter(isLeadListInclude))];
}
/** Query di `GET /api/leads`: un parametro sconosciuto, o un valore fuori dai limiti, è `422 validation_failed`. */
export const leadListQuerySchema = z.strictObject({
    q: freeTextSchema(200).optional(),
    // «Di chi»: di partenza «mine» per il livello 1, «all» per gli altri. Non allarga mai il perimetro del ruolo.
    who: z.enum(["mine", "all"]).optional(),
    /** La definizione dei filtri (Task 1), JSON nell'indirizzo; assente = nessun filtro. */
    filter: filterJsonSchema.optional(),
    /**
     * Chiavi dei campi dell'azienda da restituire in `custom` (colonne mostrate), separate da virgola, al massimo 20. Ogni
     * chiave arriva a una query: ha la forma di un identificativo, e una chiave vuota (`a,,b`) non ce l'ha. Il testo sta in
     * 900 caratteri: venti chiavi di 41 con le virgole ne fanno 839.
     */
    fields: z
        .string()
        .max(900)
        .refine((fields) => fields === "" || fields.split(",").every((key) => ID_PATTERN.test(key)), { message: "Field keys must be identifiers" })
        .refine((fields) => fields.split(",").length <= LEAD_LIST_FIELDS_MAX, { message: `At most ${LEAD_LIST_FIELDS_MAX} fields` })
        .optional(),
    /** Le colonne di `LEAD_LIST_INCLUDES` da leggere con le righe (colonne mostrate), separate da virgola. */
    include: z
        .string()
        .max(100)
        .refine((include) => include === "" || include.split(",").every(isLeadListInclude), { message: "Unknown column" })
        .optional(),
    sort: z.enum(LEAD_SORT_FIELDS).default("createdAt"),
    direction: z.enum(["asc", "desc"]).default("desc"),
    offset: listOffsetSchema,
    limit: z.coerce.number().int().min(1).max(100).default(100),
});
const namedRefSchema = z.object({ id: z.string(), name: z.string() });
/** L'owner del Lead (`personSchema` di `people.ts`, dove sta anche `actorViewSchema`). */
const ownerSchema = personSchema;
const callDirectionSchema = z.enum(["outbound", "inbound"]);
/**
 * Le sorgenti di «Provenienza»: i cinque ingressi di `lead_acquisitions` (i moduli di Meta, il sito, GoHighLevel, un file, «Nuovo
 * Lead») e Close, per i Lead importati da Close. Nella riga è un testo: una sorgente nuova del server non rompe l'elenco di
 * un'interfaccia di prima, che la scrive con una parola generica.
 */
export const LEAD_SOURCE_SYSTEMS = ["meta", "web", "ghl", "file", "manual", "close"];
/** Un ingresso del Lead: la sorgente e il nome della campagna, quando la sorgente lo dice. */
const leadSourceSchema = z.object({ system: z.string(), campaign: z.string().nullable() });
export const leadRowSchema = z.object({
    id: z.string(),
    displayName: z.string().nullable(),
    /**
     * Nome e Cognome del titolare, separati, per le colonne «Nome» e «Cognome» (decisione del 6 ottobre 2026): senza spazi in più,
     * `null` se mancano. Dal 6 ottobre 2026; una risposta di prima, senza, vale `null`.
     */
    firstName: z.string().nullable().default(null),
    lastName: z.string().nullable().default(null),
    salonName: z.string().nullable(),
    // La provincia per esteso, la sigla secondaria (decisione confermata); nomi della provincia e della regione letti al
    // momento dal catalogo ISTAT. Senza città riconosciuta (anche un indirizzo estero, importato senza città) è null.
    city: z.object({ name: z.string(), provinceName: z.string(), provinceAbbreviation: z.string(), regionName: z.string() }).nullable(),
    /** La nazione del Lead, `IT`, `CH` o null: c'è anche senza comune, come nella scheda (piano M3, D6). */
    countryCode: z.string().nullable(),
    email: z.string().nullable(),
    /** Il cellulare del titolare (e164), oppure null; quello del secondo intestatario non conta. */
    mobile: z.string().nullable(),
    /** Il fisso del titolare (e164), oppure null. */
    landline: z.string().nullable(),
    status: namedRefSchema,
    // «Ultima chiamata»: entrate e uscite (decisione confermata). `outcome` è il passo più preciso dell'esito: dopo «Risposta»,
    // com'è andata (piano M4, Task 10), come nella scheda.
    latestCall: z.object({ at: z.string(), direction: callDirectionSchema, outcome: z.string().nullable() }).nullable(),
    /**
     * «Prossima task» (piano M5, Task 20; questione 264): il richiamo aperto del Lead, con la sua ora (istante ISO) e se è
     * «Concordato con il Lead»; null senza richiamo aperto. Le chiamate della sequenza non sono Task e qui non ci sono.
     */
    nextTask: z.object({ at: z.string(), agreed: z.boolean() }).nullable(),
    // Un Lead senza nome si legge dal suo telefono (mock-up).
    primaryPhone: z.string().nullable(),
    // «Chiamate»: le uscite valide, con ogni esito, da sempre.
    outgoingCallCount: z.number().int(),
    // «Chiamate negli ultimi 12 mesi» e «negli ultimi 4 mesi» (decisione del 1° ottobre 2026): le stesse chiamate, dalla
    // mezzanotte dello stesso giorno di 12 o 4 mesi fa nel fuso dell'azienda (`recentWindowStart` del dominio), calcolate
    // a ogni richiesta.
    outgoingCallCountLast12Months: z.number().int(),
    outgoingCallCountLast4Months: z.number().int(),
    tags: z.array(namedRefSchema),
    createdAt: z.string(),
    owner: ownerSchema.nullable(),
    /**
     * Soltanto con `fields=`: chiave del campo → numero, testo, id della voce o null, per ogni chiave chiesta di un campo
     * dell'azienda non archiviato (una chiave sconosciuta non c'è).
     */
    custom: z.record(z.string(), z.union([z.number(), z.string(), z.null()])).optional(),
    /**
     * Soltanto con `include=smartLists`, «In Smart List» (LEAD-13): le Smart List in cui il Lead è adesso (entrata aperta, lista non
     * archiviata) fra quelle che chi guarda vede, nella «Guarda come…» anche la persona osservata, in ordine alfabetico italiano.
     */
    smartLists: z.array(namedRefSchema).optional(),
    /**
     * Soltanto con `include=source`, «Provenienza» (LEAD-13, INGR-5): la prima attribuzione, che non cambia, e l'ultimo ingresso se è
     * un altro (altrimenti null); null per un Lead senza provenienza (nato prima delle provenienze, non da Close).
     */
    source: z.object({ first: leadSourceSchema, latest: leadSourceSchema.nullable() }).nullable().optional(),
});
export const leadListResponseSchema = z.object({
    rows: z.array(leadRowSchema),
    /** I Lead del perimetro filtrato (ricerca e «Di chi»), non soltanto quelli della pagina. */
    total: z.number().int(),
    offset: z.number().int(),
    limit: z.number().int(),
    /** «Di chi» applicato davvero: chi ha l'ambito `own` vede sempre i propri, la piattaforma sempre tutti. */
    who: z.enum(["mine", "all"]),
    canChooseWho: z.boolean(),
});
/**
 * `GET /api/leads/catalog`: i campi dell'azienda non archiviati, per i filtri (choice e number/currency_eur) e per le
 * colonne (tutti), in ordine di creazione. Le voci nascoste ci sono, con `hidden`: un Lead può averne ancora una.
 */
export const leadCatalogResponseSchema = z.object({
    fields: z.array(z.object({
        key: z.string(),
        label: z.string(),
        type: z.enum(["number", "currency_eur", "choice", "text"]),
        options: z.array(z.object({ id: z.string(), label: z.string(), hidden: z.boolean() })),
    })),
});
/** Quanti codici di città o province si chiedono al massimo in una volta (`values=`): le voci di un filtro. */
export const FILTER_OPTION_VALUES_MAX = 200;
/**
 * Query di `GET /api/leads/filter-options` (piano M3, D4): il campo a scelta di cui contare le voci (`owner`, `status`,
 * `tags`, `latestCallOutcome`, `country`, `region`, `province`, `city` o `field:<chiave>` di tipo scelta) e, per città
 * e province, il testo cercato (almeno due caratteri) oppure, in alternativa, i codici già scelti (`values`, separati da
 * virgola, al massimo 200: decisione P20), per avere il loro nome quando un filtro arriva da un link o da una Smart
 * List. Un campo che non è a scelta, o un codice della forma sbagliata, è `422 filter_invalid`.
 */
export const filterOptionsQuerySchema = z
    .strictObject({
    field: filterFieldRefSchema,
    q: freeTextSchema(100).optional(),
    // Duecento codici di sei cifre con le virgole fanno 1.399 caratteri. Ogni codice lo controlla il server, come le
    // voci di un filtro: una forma sbagliata è `filter_invalid`, non soltanto un parametro sbagliato.
    values: z.string().max(2000).optional(),
})
    .refine((query) => query.values === undefined || query.field === "city" || query.field === "province", {
    message: "values is only for city and province",
    path: ["values"],
})
    .refine((query) => query.values === undefined || query.q === undefined, { message: "values and q exclude each other", path: ["values"] });
/**
 * Le voci di un filtro a scelta con quanti Lead visibili le hanno (perimetro di lettura, senza gli altri filtri né «Di
 * chi»). `value` è il valore della condizione; `label` il nome da mostrare, vuoto dove lo mette l'interfaccia («Senza
 * owner», gli esiti). `truncated`: città e province hanno più voci delle trenta date.
 */
export const filterOptionsResponseSchema = z.object({
    options: z.array(z.object({ value: z.string(), label: z.string(), count: z.number().int() })),
    truncated: z.boolean(),
});
export const leadPhoneSchema = z.object({
    id: z.string(),
    holder: z.enum(["primary", "second"]),
    kind: z.enum(["mobile", "landline"]),
    e164: z.string(),
});
export const LEAD_CARD_FIELD_TYPES = ["system", "number", "currency_eur", "choice", "text"];
/**
 * Lo status del Lead nella scheda, con la chiave stabile degli status di partenza (`null` per quelli creati dall'azienda) e
 * se è lo status di partenza: la postazione di chiamata chiede lo status finché il Lead ha quello (piano M4, Task 13,
 * questione 160), dalla stessa lettura della scheda. Dal 4 ottobre 2026; una risposta di prima, senza, vale `null` e `false`.
 */
export const cardLeadStatusSchema = namedRefSchema.extend({ key: z.string().nullable().default(null), isDefault: z.boolean().default(false) });
/**
 * «Prossimo passo» della scheda (piano M5, D12, questione 264): il prossimo tentativo dovuto della sequenza di chiamate in corso
 * («Chiamata della sequenza · tentativo {n} di {total} · {quando}»), con la sua mezza giornata (`AAAA-MM-GG:m|p`, la mattina o il
 * pomeriggio) e l'istante da cui è dovuto (per il primo tentativo l'ingresso del Lead o, se è entrato fuori orario, l'apertura
 * della mezza giornata dopo; per gli altri l'inizio della loro mezza giornata); `dueNow` quando è già dovuto («adesso»). Le
 * parole le scrive l'interfaccia, nel fuso dell'azienda. Da R3 (piano M5, Task 16) il richiamo aperto viene prima: «Richiamo ·
 * {quando}», con il Task, la sua scadenza e se è concordato con il Lead (il simbolo dell'orologio).
 */
export const leadNextStepSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("sequence_attempt"),
        attempt: z.number().int(),
        total: z.number().int(),
        halfDay: z.string(),
        dueAt: z.string(),
        dueNow: z.boolean(),
    }),
    z.object({ kind: z.literal("callback"), taskId: z.string(), dueAt: z.string(), agreedWithLead: z.boolean() }),
]);
/** Perché due Lead sono una coppia di possibili doppioni (LEAD-27): lo stesso cellulare (`phone`) o la stessa email (`email`). */
export const DUPLICATE_REASONS = ["phone", "email"];
/** Quanti possibili doppioni la scheda nomina, una riga ciascuno (LEAD-28); gli altri li conta `total` («e altri {n}»). */
export const CARD_POSSIBLE_DUPLICATES_SHOWN = 3;
/**
 * Un possibile doppione del Lead sulla scheda (LEAD-28), con lo stesso criterio di «Pulizia dati» (LEAD-27): l'altro Lead, che chi
 * guarda vede, con il suo nome (o salone, o telefono) e il motivo. `canResolve` dice se chi guarda può unirli o segnarli «Non sono
 * doppioni»: la funzione `merge_duplicates` e la modifica di tutti e due i Lead, come le rotte di «Pulizia dati».
 */
export const possibleDuplicateSchema = z.object({
    leadId: z.string(),
    displayName: z.string().nullable(),
    salonName: z.string().nullable(),
    primaryPhone: z.string().nullable(),
    reason: z.enum(DUPLICATE_REASONS),
    canResolve: z.boolean(),
});
export const leadCardResponseSchema = z.object({
    lead: z.object({
        id: z.string(),
        version: z.number().int(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        salonName: z.string().nullable(),
        email: z.string().nullable(),
        website: z.string().nullable(),
        secondHolder: z
            .object({ firstName: z.string().nullable(), lastName: z.string().nullable(), email: z.string().nullable(), mobile: z.string().nullable() })
            .nullable(),
        phones: z.array(leadPhoneSchema),
        status: cardLeadStatusSchema,
        owner: ownerSchema.nullable(),
        tags: z.array(namedRefSchema),
        // Le righe Provincia, Regione e Nazione della scheda (`system:province`, `system:region`, `system:country`) si leggono
        // da qui e non si scrivono mai da sé: vengono dalla città.
        place: z
            .object({
            cityIstatCode: z.string(),
            cityName: z.string(),
            provinceName: z.string(),
            provinceAbbreviation: z.string(),
            regionName: z.string(),
            countryCode: z.string(),
        })
            .nullable(),
        /**
         * La nazione del Lead (`IT`, `CH` o `null`), che c'è anche senza comune: la riga Nazione la mostra quando manca la
         * città. Dal 1° ottobre 2026; una risposta di prima, senza, vale `null`.
         */
        countryCode: z.string().nullable().default(null),
        streetAddress: z.string().nullable(),
        postalCode: z.string().nullable(),
        /** Chiave del campo dell'azienda → numero, testo o id della voce; soltanto i campi visibili del modello. */
        custom: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
        createdAt: z.string(),
        createdBy: actorViewSchema,
        updatedAt: z.string(),
        updatedBy: actorViewSchema,
    }),
    model: z.object({
        version: z.number().int(),
        sections: z.array(z.object({
            key: z.enum(["contact", "salon", "territory"]),
            fields: z.array(z.object({
                ref: z.string(),
                // null per i campi di sistema: l'etichetta viene da i18n.
                label: z.string().nullable(),
                type: z.enum(LEAD_CARD_FIELD_TYPES),
                options: z.array(z.object({ id: z.string(), label: z.string() })).optional(),
                editable: z.boolean(),
            })),
        })),
    }),
    stats: z.object({
        outgoingCallCount: z.number().int(),
        callCount: z.number().int(),
        latestCall: z
            .object({ at: z.string(), direction: callDirectionSchema, outcome: z.string().nullable(), authorDisplayName: z.string().nullable() })
            .nullable(),
    }),
    permissions: z.object({
        canUpdate: z.boolean(),
        canChangeOwner: z.boolean(),
        canManageTags: z.boolean(),
        canAddNotes: z.boolean(),
        /**
         * «Archivia» (piano M3, Task 11): la funzione `trash_restore` e la modifica del Lead. Dal 2 ottobre 2026; una
         * risposta di prima, senza, vale `false`.
         */
        canArchive: z.boolean().default(false),
        /**
         * «Chiama» (piano M4, Task 8; questione 164): la funzione `call`, la modifica del Lead, un'appartenenza (non il contesto
         * di piattaforma) e fuori da «Guarda come…». Dal 3 ottobre 2026; una risposta di prima, senza, vale `false`.
         */
        canCall: z.boolean().default(false),
    }),
    // «Questo Lead è di …» quando l'owner non è chi guarda.
    ownerNotice: z.object({ ownerDisplayName: z.string() }).nullable(),
    /**
     * «Chiama» telefono per telefono, con il motivo quando è spento, e chi è al telefono con il Lead (piano M4, Task 8, D9).
     * Dal 3 ottobre 2026; una risposta di prima, senza, non chiama nessun telefono.
     */
    calling: leadCallingSchema.default(() => ({ ...NO_CALLING, phones: [] })),
    /**
     * Il Lead è aperto nel Power Dialer di un collega, che lo tiene riservato (piano M5, D2): il suo nome. `null` se nessuno lo
     * tiene o se lo tiene chi guarda. Dal 4 ottobre 2026; una risposta di prima, senza, vale `null`.
     */
    reservation: z.object({ byDisplayName: z.string() }).nullable().default(null),
    /**
     * La sequenza di chiamate del Lead (piano M5, Task 11, D8, D16): quella in corso o l'ultima finita, con le caselle, il giudizio
     * e i comandi di chi guarda; `null` se il Lead non ne ha mai avuta una (un Lead di prima di M5, questione 256). Dal 4 ottobre
     * 2026; una risposta di prima, senza, vale `null`.
     */
    sequence: callSequenceViewSchema.nullable().default(null),
    /**
     * «Prossimo passo» (`leadNextStepSchema`): il richiamo aperto oppure, senza, il prossimo tentativo della sequenza in corso; `null`
     * senza nessuno dei due. Dal 4 ottobre 2026, come `sequence`.
     */
    nextStep: leadNextStepSchema.nullable().default(null),
    /**
     * Il richiamo aperto del Lead (piano M5, Task 16, D12: al più uno), a chiunque sia assegnato: «Richiama dopo» su un Lead che ne ha
     * già uno risponde `409 task_exists`, e la scheda offre «Sposta». `null` senza. Dal 4 ottobre 2026; una risposta di prima, senza,
     * vale `null`.
     */
    openTask: taskViewSchema.nullable().default(null),
    /**
     * «WhatsApp» e «Non inviare WhatsApp» (piano M6, R1, Task 3b; ruling B5, D25): se chi guarda può preparare un WhatsApp a questo
     * Lead o il primo motivo per cui no, a quali cellulari si scrive, lo stato dell'interruttore e chi lo può accendere o spegnere
     * (`leadWhatsAppSchema`). Dal 5 ottobre 2026; una risposta di prima, senza, non manda WhatsApp.
     */
    whatsapp: leadWhatsAppSchema.default(() => ({ ...NO_LEAD_WHATSAPP, phoneIds: [] })),
    /**
     * I possibili doppioni del Lead (LEAD-28): al più `CARD_POSSIBLE_DUPLICATES_SHOWN`, prima per cellulare e poi per email, e quanti
     * sono in tutto. Dal 10 ottobre 2026; una risposta di prima, senza, non ne ha.
     */
    possibleDuplicates: z.object({ total: z.number().int(), items: z.array(possibleDuplicateSchema) }).default(() => ({ total: 0, items: [] })),
});
/**
 * `GET /api/lead-statuses`: soltanto gli status che si scelgono, in ordine. `key` è la chiave stabile degli status di
 * partenza (`no_type`, `numero_errato`, …; `null` per quelli creati dall'azienda) e `isDefault` lo status che un Lead nuovo
 * riceve: la postazione di chiamata chiede lo status finché il Lead ha quello di partenza e offre «Metti «Numero Errato»»
 * dalla chiave (piano M4, Task 13, questione 160). Dal 4 ottobre 2026; una risposta di prima, senza, vale `null` e `false`.
 */
export const leadStatusesResponseSchema = z.object({
    statuses: z.array(z.object({
        id: z.string(),
        name: z.string(),
        position: z.number().int(),
        selectable: z.boolean(),
        key: z.string().nullable().default(null),
        isDefault: z.boolean().default(false),
    })),
});
/** `GET /api/tags`: i tag non nascosti, in ordine alfabetico italiano. */
export const tagsResponseSchema = z.object({ tags: z.array(namedRefSchema) });
/** La lunghezza massima del nome di un tag, come quella dei tag che arrivano dagli ingressi. */
export const TAG_NAME_MAX = 100;
// Spazi (anche non separabili), tab e a capo di fila: uno spazio solo, prima dei controlli e della lunghezza. Non i caratteri
// invisibili (BOM, spazio di larghezza zero), che il testo su una riga rifiuta.
const TAG_NAME_SPACES = /[\p{Zs}\t\n\v\f\r\u0085\u2028\u2029]+/gu;
/**
 * Il nome di un tag scritto a mano: un testo su una riga con gli spazi di fila ridotti a uno, da 1 a `TAG_NAME_MAX` caratteri. Il
 * server rifiuta poi un nome senza lettere né cifre o che comincia con «Owner:» (`tag_name_invalid`); il web lo usa per proporre
 * «Crea il tag «…»» soltanto per un nome che il server accetta.
 */
export const tagNameSchema = z
    .string()
    .transform((value) => value.replace(TAG_NAME_SPACES, " "))
    .pipe(singleLineTextSchema(TAG_NAME_MAX));
/** `POST /api/tags`: un tag nuovo per nome, con la funzione `manage_tags` (24/10/2026). */
export const createTagBodySchema = z.strictObject({ name: tagNameSchema });
/** La risposta di `POST /api/tags`: il tag, nuovo (`created`) oppure quello che c'era già con lo stesso nome. */
export const createTagResponseSchema = z.object({ tag: namedRefSchema, created: z.boolean() });
/** `GET /api/lead-owners/options`: chi può essere owner di un Lead. */
export const leadOwnerOptionsResponseSchema = z.object({ owners: z.array(z.object({ membershipId: z.string(), displayName: z.string() })) });
/** Query di `GET /api/geography/municipalities`: sotto i due caratteri nessun risultato. */
export const municipalitySearchQuerySchema = z.strictObject({ q: freeTextSchema(100).default("") });
export const municipalityMatchSchema = z.object({ istatCode: z.string(), label: z.string(), provinceName: z.string(), regionName: z.string() });
/** Al massimo otto comuni. */
export const municipalitiesResponseSchema = z.object({ municipalities: z.array(municipalityMatchSchema) });
// ---------------------------------------------------------------------------------------------------------------
// Scritture dei Lead (piano M2, Task 7): modifiche sulla scheda, owner, tag, note e «Nuovo Lead». Il server
// normalizza e valida ogni valore (telefoni, città, voci, euro) e confronta con quelli attuali: un salvataggio
// senza differenze non scrive nulla.
const phoneValueSchema = z.string().max(40).nullable();
/**
 * La lunghezza massima di un'email del Lead e del secondo titolare, in tutte le scritture; la usa anche la pre-selezione
 * di «Pulizia dati» (decisione P61): un'email più lunga la scheda non la scrive, quindi resta da controllare.
 */
export const LEAD_EMAIL_MAX_LENGTH = 254;
/**
 * Un posto dei telefoni: intestatario e tipo; `null` (o solo spazi) lo svuota. Il titolare ha cellulare e fisso, il
 * secondo titolare soltanto il cellulare (decisione confermata: nome e cognome, cellulare, email).
 */
const phoneSlotSchema = z.union([
    z.strictObject({ holder: z.literal("primary"), kind: z.enum(["mobile", "landline"]), value: phoneValueSchema }),
    z.strictObject({ holder: z.literal("second"), kind: z.literal("mobile"), value: phoneValueSchema }),
]);
/**
 * `PATCH /api/leads/:leadId`: la versione letta e soltanto i campi che cambiano. Provincia, regione e nazione non
 * si scrivono: seguono la città. `secondHolder: null` toglie il secondo intestatario con i suoi telefoni.
 */
export const leadPatchBodySchema = z.strictObject({
    version: z.number().int().min(1),
    changes: z.strictObject({
        firstName: leadNameSchema(120).nullable().optional(),
        lastName: leadNameSchema(120).nullable().optional(),
        salonName: leadNameSchema(200).nullable().optional(),
        email: z.email().max(LEAD_EMAIL_MAX_LENGTH).nullable().optional(),
        // Il sito si mostra come un collegamento: un testo su una riga, come un nome (questione aperta n. 92).
        website: leadNameSchema(500).nullable().optional(),
        secondHolder: z
            .strictObject({ firstName: leadNameSchema(120).nullable(), lastName: leadNameSchema(120).nullable(), email: z.email().max(LEAD_EMAIL_MAX_LENGTH).nullable() })
            .nullable()
            .optional(),
        phones: z.array(phoneSlotSchema).max(4).optional(),
        statusId: idSchema.optional(),
        // Il codice ISTAT si confronta con il catalogo in memoria prima di ogni scrittura: non arriva mai a una query com'è.
        cityIstatCode: z.string().nullable().optional(),
        streetAddress: freeTextSchema(300).nullable().optional(),
        postalCode: freeTextSchema(20).nullable().optional(),
        // La chiave del campo dell'azienda arriva a una query: ha la forma di un identificativo (`422` sul campo `custom`).
        custom: z
            .record(z.string(), z.union([z.number(), freeTextSchema(2000), z.null()]))
            .refine((custom) => Object.keys(custom).every((key) => ID_PATTERN.test(key)), { message: "Field keys must be identifiers" })
            .optional(),
    }),
});
/** `POST /api/leads/:leadId/owner`: con la funzione `change_owner`; `null` toglie l'owner; il motivo è obbligatorio. */
export const changeOwnerBodySchema = z.strictObject({ version: z.number().int().min(1), ownerMembershipId: idSchema.nullable(), reason: multiLineTextSchema(500) });
/** `PUT /api/leads/:leadId/tags`: l'insieme intero dei tag del Lead, con la funzione `manage_tags`. */
export const setTagsBodySchema = z.strictObject({ version: z.number().int().min(1), tagIds: z.array(idSchema).max(200) });
/** `POST /api/leads/:leadId/notes`: una nota, o l'appunto di una chiamata dello stesso Lead. */
export const addNoteBodySchema = z.strictObject({ body: multiLineTextSchema(10000), callId: idSchema.optional() });
/** `POST /api/leads`: «Nuovo Lead». */
export const createLeadBodySchema = z.strictObject({
    firstName: leadNameSchema(120).optional(),
    lastName: leadNameSchema(120).optional(),
    salonName: leadNameSchema(200).optional(),
    mobile: z.string().max(40).optional(),
    landline: z.string().max(40).optional(), // almeno uno dei due: il telefono è il solo dato obbligatorio
    email: z.email().max(LEAD_EMAIL_MAX_LENGTH).optional(),
    cityIstatCode: z.string().optional(),
    ownerMembershipId: idSchema.nullable().optional(),
    acknowledgePossibleDuplicate: z.boolean().optional(),
});
/**
 * I dettagli di `409 phone_in_use`: il Lead che ha già quel numero attivo, se chi agisce può aprirne la scheda e il numero
 * normalizzato. `archived` (piano M3, Task 11; D9, questione 67): il numero è di un Lead archiviato, che conta ancora nel
 * controllo dei doppioni e non ha scheda (`canOpen` è falso): l'interfaccia porta agli «Archiviati» per ripristinarlo. Fra
 * più Lead con lo stesso numero il server indica prima un Lead attivo (decisione P33).
 */
export const phoneInUseDetailsSchema = z.object({ leadId: z.string(), canOpen: z.boolean(), e164: z.string(), archived: z.boolean() });
/**
 * I dettagli di `409 possible_duplicate_email` («Nuovo Lead»): il Lead che ha già quell'email, se chi agisce può aprirlo e,
 * come per i telefoni (decisione P33 del piano M3), se è archiviato. Fra più Lead con la stessa email indica prima un attivo.
 */
export const duplicateEmailDetailsSchema = z.object({ leadId: z.string(), canOpen: z.boolean(), archived: z.boolean() });
/** Risposta di modifica, owner e tag: il Lead come nella scheda, con la versione da cui ripartire. */
export const leadWriteResponseSchema = z.object({ lead: leadCardResponseSchema.shape.lead });
/**
 * `PUT /api/leads/:leadId/whatsapp-opt-out` (piano M6, R1, Task 3b; D25): il Lead dopo, come dopo ogni scrittura, con il suo
 * WhatsApp della scheda (`whatsapp`: l'interruttore, il motivo di «WhatsApp» spento e chi può spegnerlo).
 */
export const whatsAppOptOutResponseSchema = z.object({ lead: leadCardResponseSchema.shape.lead, whatsapp: leadWhatsAppSchema });
/** `POST /api/leads` risponde 201 con l'identificativo: la scheda si apre con la sua lettura. */
export const createLeadResponseSchema = z.object({ lead: z.object({ id: z.string() }) });
/** Una nota del Lead come la scrive Gioia365: l'autore è l'istantanea di chi l'ha scritta. */
export const leadNoteSchema = z.object({
    id: z.string(),
    leadId: z.string(),
    callId: z.string().nullable(),
    body: z.string(),
    occurredAt: z.string(),
    author: actorViewSchema,
    source: z.enum(["gioia360", "close_import"]), // il valore salvato nel database, come `LEAD_NOTE_SOURCES`
});
/** `POST /api/leads/:leadId/notes` risponde 201 con la nota. */
export const addNoteResponseSchema = z.object({ note: leadNoteSchema });
// ---------------------------------------------------------------------------------------------------------------
// I Lead di chi viene sospeso o archiviato (piano M2, Task 10; spec §6). Il segnale dei Lead da riassegnare è
// derivato e non si scrive: un Lead il cui owner non è attivo compare nei conteggi e ha `owner.active = false`.
/** `GET /api/lead-owners/counts`: per ogni owner, i suoi Lead non archiviati dentro il perimetro di chi chiede. */
export const ownerCountsResponseSchema = z.object({ counts: z.array(z.object({ membershipId: z.string(), leads: z.number().int() })) });
/** `POST /api/memberships/:membershipId/reassign-leads`: «Riassegnali agli altri User, a turno». */
export const reassignLeadsBodySchema = z.strictObject({ mode: z.literal("round_robin") });
/** Quanti Lead sono passati e a chi, in ordine di nome; chi non ne ha ricevuti non compare. */
export const reassignLeadsResponseSchema = z.object({
    reassigned: z.number().int(),
    perOwner: z.array(z.object({ membershipId: z.string(), leads: z.number().int() })),
});
// ---------------------------------------------------------------------------------------------------------------
// Archivio dei Lead (piano M3, Task 11; D9): «Archivia» sulla scheda, «Ripristina» ed «Elimina per sempre» dagli
// «Archiviati». Un Lead archiviato non ha scheda e non entra in elenchi, Smart List e conteggi; il suo numero conta
// ancora nei doppioni (`phoneInUseDetailsSchema`). Un Lead unito a un altro con «Pulizia dati» compare fra gli archiviati
// con il Lead in cui è stato unito, ma non si ripristina (`422 lead_merged`, questione 140).
/** `POST /api/leads/:leadId/archive`: con la funzione `trash_restore` e la modifica del Lead; il motivo è facoltativo. */
export const archiveLeadBodySchema = z.strictObject({ version: z.number().int().min(1), reason: multiLineTextSchema(500).optional() });
/** `POST /api/leads/:leadId/restore`: con la funzione `trash_restore` e la modifica del Lead. */
export const restoreLeadBodySchema = z.strictObject({ version: z.number().int().min(1) });
/** «Elimina per sempre»: la parola scritta dal Super admin, come nel mock-up («Sì, elimina per sempre» dopo una conferma scritta). */
export const deleteLeadBodySchema = z.strictObject({ confirmation: z.literal("elimina") });
/** Query di `GET /api/leads/archived`: la ricerca come quella dell'elenco, dal più recente, a pagine di 50. */
export const archivedLeadListQuerySchema = z.strictObject({
    q: freeTextSchema(200).optional(),
    offset: listOffsetSchema,
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * Un Lead archiviato: chi lo ha archiviato, quando e perché. `mergedInto` è il Lead in cui è stato unito («Unito con
 * «…»», non si ripristina); il suo nome è `null` quando quel Lead è fuori dal perimetro di chi guarda. `canRestore`
 * (decisione P36 del piano M3): chi guarda può ripristinare proprio questo Lead, cioè lo può modificare (oltre alla
 * funzione `trash_restore`, che la rotta ha già controllato) e non è unito a un altro. Una risposta di prima non lo porta:
 * vale falso, e «Ripristina» non compare.
 */
export const archivedLeadRowSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    displayName: z.string().nullable(),
    salonName: z.string().nullable(),
    primaryPhone: z.string().nullable(),
    owner: ownerSchema.nullable(),
    archivedAt: z.string(),
    archivedBy: actorViewSchema,
    reason: z.string().nullable(),
    mergedInto: z.object({ id: z.string(), displayName: z.string().nullable() }).nullable(), // «Unito con «…»»: non si ripristina
    canRestore: z.boolean().default(false),
});
/**
 * `canRestore`: la funzione `trash_restore` (la rotta la richiede: è sempre vero); se un Lead si ripristina lo dice la sua
 * riga. `canDelete`: l'ambito `delete` `all` dei Lead (il Super admin dell'azienda).
 */
export const archivedLeadListResponseSchema = z.object({
    rows: z.array(archivedLeadRowSchema),
    total: z.number().int(),
    offset: z.number().int(),
    limit: z.number().int(),
    canRestore: z.boolean(),
    canDelete: z.boolean(),
});
/** `DELETE /api/leads/:leadId` risponde quante righe della storia del Lead sono state tolte con lui. */
export const deleteLeadResponseSchema = z.object({ deleted: z.object({ events: z.number().int(), notes: z.number().int(), calls: z.number().int(), phones: z.number().int() }) });
// ---------------------------------------------------------------------------------------------------------------
// Azioni multiple (piano M3, Task 13; D10): «Cambia owner», «Cambia status», «Aggiungi tag», «Togli tag» e «Archivia» su
// più Lead in una volta. Il gruppo lo fissa il server al momento della conferma, dentro il perimetro di modifica di chi
// agisce: gli id scelti, oppure «Seleziona tutti i N risultati», cioè la vista di adesso (ricerca, «Di chi» e filtri) con
// la stessa condizione dell'elenco. Ogni Lead cambia con le regole della sua scrittura singola e un evento suo.
export const BULK_ACTIONS = ["change_owner", "change_status", "add_tag", "remove_tag", "archive"];
/** Al massimo 5.000 Lead per operazione: oltre, `422 bulk_too_large` con `details.count`, prima di bloccare i Lead. */
export const BULK_MAX_LEADS = 5000;
export const bulkTargetSchema = z.union([
    z.strictObject({ kind: z.literal("ids"), ids: z.array(idSchema).min(1).max(BULK_MAX_LEADS) }),
    /** «Seleziona tutti i N risultati»: la vista di adesso; `expectedTotal` è il numero che la persona ha visto. */
    z.strictObject({
        kind: z.literal("query"),
        q: freeTextSchema(200).optional(),
        who: z.enum(["mine", "all"]).optional(),
        filter: filterDefinitionSchema.optional(),
        expectedTotal: z.number().int().min(0),
    }),
]);
const bulkBase = { target: bulkTargetSchema };
/**
 * `POST /api/leads/bulk`: serve la funzione `bulk_actions` e, per azione, la funzione della scrittura singola
 * (`change_owner` con il motivo obbligatorio, `manage_tags`, `trash_restore`); «Cambia status» vuole soltanto la modifica.
 */
export const bulkActionBodySchema = z.discriminatedUnion("action", [
    z.strictObject({ action: z.literal("change_owner"), ...bulkBase, ownerMembershipId: idSchema.nullable(), reason: multiLineTextSchema(500) }),
    z.strictObject({ action: z.literal("change_status"), ...bulkBase, statusId: idSchema }),
    z.strictObject({ action: z.literal("add_tag"), ...bulkBase, tagId: idSchema }),
    z.strictObject({ action: z.literal("remove_tag"), ...bulkBase, tagId: idSchema }),
    z.strictObject({ action: z.literal("archive"), ...bulkBase, reason: multiLineTextSchema(500).optional() }),
]);
/** Un owner non idoneo non è un salto Lead per Lead: è un solo `422 owner_not_eligible` prima di scrivere. */
export const BULK_SKIP_REASONS = ["permission_denied", "not_found"];
/**
 * Com'è andata: `candidates` sono i Lead del gruppo (gli id distinti scelti, oppure quelli che rispondono alla ricerca
 * adesso, che possono essere più o meno di `expectedTotal`); `affected` quelli cambiati, `unchanged` quelli già nello
 * stato chiesto, `skipped` gli id scelti che chi agisce non vede (`not_found`) o vede ma non modifica (`permission_denied`).
 */
export const bulkActionResponseSchema = z.object({
    operationId: z.string(),
    candidates: z.number().int(),
    affected: z.number().int(),
    unchanged: z.number().int(),
    skipped: z.array(z.object({ leadId: z.string(), reason: z.enum(BULK_SKIP_REASONS) })),
});
// ---------------------------------------------------------------------------------------------------------------
// «Pulizia dati» → «Doppioni» (piano M3, Task 16; D11): le coppie di Lead attivi con lo stesso cellulare o la stessa email,
// «Non sono doppioni» e «Unisci in un solo Lead». Una coppia vale per tutta l'azienda e sta dentro il perimetro di modifica
// di chi guarda (tutti e due i Lead). Resta il Lead con l'ultima chiamata più recente; a parità, o senza chiamate, quello
// aggiornato più di recente; a parità ancora, quello creato prima (questione 139). L'unione è una transazione sola, che
// applica i valori scelti campo per campo con le regole della scheda e ricollega chiamate, note ed eventi al Lead che resta.
/**
 * I campi che la tabella dell'unione fa scegliere, oltre ai campi dell'azienda (`field:<chiave>`): nomi, salone, cellulare e
 * fisso del titolare, email, città, status e owner. Un campo che non si nomina resta quello del Lead che resta.
 */
export const MERGE_FIELD_KEYS = ["firstName", "lastName", "salonName", "mobile", "landline", "email", "city", "status", "owner"]; // + "field:<chiave>" per i campi dell'azienda
/** Il prefisso della chiave di un campo dell'azienda nella tabella dell'unione. */
export const MERGE_CUSTOM_FIELD_PREFIX = "field:";
/** Il valore di un campo di un Lead nella tabella: com'è salvato (`raw`) e come si legge (`label`: status, owner, città, voce). */
export const mergeValueSchema = z.object({ raw: z.union([z.string(), z.number(), z.null()]), label: z.string().nullable() }); // label: il nome dello status, dell'owner, della città, della voce
/** Un Lead di una coppia, con le chiamate per riconoscerlo e i valori di ogni campo che si sceglie (`values`). */
export const mergeCandidateSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    displayName: z.string().nullable(),
    salonName: z.string().nullable(),
    primaryPhone: z.string().nullable(),
    latestCallAt: z.string().nullable(),
    createdAt: z.string(),
    outgoingCallCount: z.number().int(),
    callCount: z.number().int(),
    tags: z.array(namedRefSchema),
    values: z.record(z.string(), mergeValueSchema),
});
/**
 * Una coppia di doppioni: `key` è quella di «Non sono doppioni» (`pair:<idMinore>:<idMaggiore>`); `reason` dice se i due Lead
 * hanno lo stesso numero (`value` è il numero) o la stessa email (`value` è l'email in minuscolo e senza spazi). `survivor` è il
 * Lead che resta secondo la regola, `absorbed` quello che entra in lui.
 */
export const duplicateGroupSchema = z.object({ key: z.string(), reason: z.enum(DUPLICATE_REASONS), value: z.string(), survivor: mergeCandidateSchema, absorbed: mergeCandidateSchema });
/** `GET /api/leads/duplicates`: le coppie a pagine di 20 (al massimo 50). */
export const duplicatesQuerySchema = z.strictObject({ offset: listOffsetSchema, limit: z.coerce.number().int().min(1).max(50).default(20) });
/** Le coppie della pagina, per motivo (prima i numeri, poi le email) e poi per nome del Lead che resta; `total` conta le coppie. */
export const duplicatesResponseSchema = z.object({ groups: z.array(duplicateGroupSchema), total: z.number().int(), offset: z.number().int(), limit: z.number().int() });
/**
 * `GET /api/leads/duplicates/pair`: una coppia sola, per «Unisci» dalla scheda (LEAD-28), con la stessa forma delle coppie di
 * «Pulizia dati» (`duplicateGroupSchema`). 422 `merge_invalid` con `not_duplicates` se i due Lead adesso non sono una coppia.
 */
export const duplicatePairQuerySchema = z.strictObject({ first: idSchema, second: idSchema });
/** `POST /api/leads/duplicates/dismiss`: «Non sono doppioni» per una coppia, per tutta l'azienda (questione 143); 204. */
export const dismissDuplicateBodySchema = z.strictObject({ leadIds: z.tuple([idSchema, idSchema]) });
/**
 * `POST /api/leads/merge`: «Unisci in un solo Lead». Le versioni lette dei due Lead e, per ogni campo che si sceglie, da quale
 * Lead prendere il valore (`survivor` o `absorbed`); i campi non nominati restano quelli del Lead che resta.
 */
export const mergeLeadsBodySchema = z.strictObject({
    survivorId: idSchema,
    absorbedId: idSchema,
    expectedVersions: z.strictObject({ survivor: z.number().int().min(1), absorbed: z.number().int().min(1) }),
    choices: z.record(z.string(), z.enum(["survivor", "absorbed"])),
});
/**
 * Com'è andata: `alreadyMerged` quando la stessa coppia era già unita nello stesso verso (nulla è cambiato); `moved` dice quante
 * chiamate, note, eventi, numeri e tag sono passati al Lead che resta.
 */
export const mergeLeadsResponseSchema = z.object({
    survivorId: z.string(),
    absorbedId: z.string(),
    alreadyMerged: z.boolean(),
    moved: z.object({ calls: z.number().int(), notes: z.number().int(), events: z.number().int(), phones: z.number().int(), tags: z.number().int() }),
});
/**
 * I motivi di `422 merge_invalid` (in `details.reason`): lo stesso Lead due volte, il Lead che resta non è quello della regola,
 * una scelta su un campo che la tabella non ha, il Lead da assorbire già unito a un altro, i due Lead che adesso non sono una
 * coppia di doppioni (`not_duplicates`: né lo stesso cellulare né la stessa email, oppure segnati «Non sono doppioni»;
 * decisione P57).
 */
export const MERGE_INVALID_REASONS = ["same_lead", "survivor_rule", "unknown_choice", "absorbed_merged_elsewhere", "not_duplicates"];
// ---------------------------------------------------------------------------------------------------------------
// «Pulizia dati» → «Telefoni ed email» (piano M3, Task 17; D12, questione 143): i dati salvati da sistemare, con la proposta
// quando il server la sa fare. Niente cambia senza l'OK: «Correggi» e «Correggi tutte le proposte» mandano le chiavi delle
// righe e le versioni lette, e il server ricalcola ogni proposta prima di applicarla con le regole della scheda; «Tieni così»
// esclude quella riga finché il valore non cambia. Tutto dentro il perimetro di modifica di chi guarda.
/** I posti che si controllano: cellulare e fisso del titolare, cellulare del secondo titolare, le due email. */
export const DATA_ISSUE_FIELDS = ["mobile", "landline", "secondMobile", "email", "secondHolderEmail"];
/** I problemi: i tre dei telefoni con proposta, la lunghezza impossibile senza proposta, e i tre delle email. */
export const DATA_ISSUE_PROBLEMS = ["landline_in_mobile_slot", "mobile_in_landline_slot", "raw_now_valid", "length_out_of_range", "case_or_spaces", "domain_typo", "invalid"];
/**
 * Una riga da sistemare: `key` è `issue:<leadId>:<campo>:<valore>` (cambia quando il valore cambia, così «Tieni così» vale per
 * quel valore); `current` il valore salvato; `proposal` il valore proposto, o `null` («Da verificare con il Lead»); `move` il
 * posto in cui il numero si sposta, quando la proposta è uno spostamento.
 */
export const dataIssueSchema = z.object({
    key: z.string(),
    leadId: z.string(),
    version: z.number().int(),
    displayName: z.string().nullable(),
    salonName: z.string().nullable(),
    field: z.enum(DATA_ISSUE_FIELDS),
    current: z.string(),
    problem: z.enum(DATA_ISSUE_PROBLEMS),
    proposal: z.string().nullable(),
    move: z.enum(["mobile", "landline"]).nullable(),
});
/** `GET /api/leads/data-issues`: le righe a pagine di 50 (al massimo 100). */
export const dataIssuesQuerySchema = z.strictObject({ offset: listOffsetSchema, limit: z.coerce.number().int().min(1).max(100).default(50) });
/** Le righe della pagina, per nome del Lead; `total` conta tutte le righe, `fixable` quelle con una proposta. */
export const dataIssuesResponseSchema = z.object({ issues: z.array(dataIssueSchema), total: z.number().int(), fixable: z.number().int(), offset: z.number().int(), limit: z.number().int() });
/**
 * La lunghezza massima di una chiave: `issue:` con l'identificativo del Lead (128), il campo e un'email fino a 254 caratteri
 * (decisione P48 del piano M3: con 200 un'email lunga non si correggerebbe né si terrebbe mai).
 */
export const DATA_ISSUE_KEY_MAX = 600;
/** Quante righe corregge al massimo una richiesta: «Correggi tutte le proposte» oltre questo numero manda più richieste. */
export const DATA_ISSUE_FIX_MAX = 500;
/** `POST /api/leads/data-issues/fix`: «Correggi» e «Correggi tutte le proposte», con la chiave di ogni riga e la versione letta. */
export const fixDataIssuesBodySchema = z.strictObject({
    fixes: z
        .array(z.strictObject({ key: z.string().max(DATA_ISSUE_KEY_MAX), leadId: idSchema, version: z.number().int().min(1) }))
        .min(1)
        .max(DATA_ISSUE_FIX_MAX),
});
/**
 * Perché una riga non si è corretta: il Lead è cambiato dopo la lettura, la riga non c'è più (valore cambiato, Lead archiviato
 * o fuori dalla vista), il Lead si vede ma non si modifica, il posto dove spostare il numero è occupato, la proposta non passa
 * le regole della scheda (o la riga non ne ha una).
 */
export const FIX_SKIP_REASONS = ["version_conflict", "no_longer_an_issue", "permission_denied", "slot_taken", "invalid"];
export const fixDataIssuesResponseSchema = z.object({ fixed: z.number().int(), skipped: z.array(z.object({ key: z.string(), reason: z.enum(FIX_SKIP_REASONS) })) });
/** `POST /api/leads/data-issues/dismiss`: «Tieni così» per una riga, per tutta l'azienda; 204, anche la seconda volta. */
export const dismissDataIssueBodySchema = z.strictObject({ key: z.string().max(DATA_ISSUE_KEY_MAX) });
//# sourceMappingURL=leads.js.map